import React, { useState } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, UserPlus, Copy, Eye, EyeOff } from "lucide-react";
import { L, Toggle, genPassword, INP_STYLE } from "./shared";

export default function NewClientUserDialog({ companies, defaultCompanyId = "", defaultProjectIds = [], projects = [], onClose, onCreated }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", job_title: "", client_company_id: defaultCompanyId, company: "", password: genPassword(), notify: false });
  const [projIds, setProjIds] = useState(new Set(defaultProjectIds));
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  const [reveal, setReveal] = useState(true);
  const [newCompany, setNewCompany] = useState(false);

  async function submit() {
    if (!f.name.trim() || !f.email.trim() || !f.password) { toast.error("Nume, email și parolă obligatorii"); return; }
    if (!newCompany && !f.client_company_id) { toast.error("Alege compania sau creează una nouă"); return; }
    if (newCompany && !f.company.trim()) { toast.error("Numele companiei noi este obligatoriu"); return; }
    setBusy(true);
    try {
      const payload = { ...f, client_company_id: newCompany ? null : f.client_company_id, company: newCompany ? f.company : "", project_ids: [...projIds] };
      await api.post("/staff/clients", payload);
      setCreated({ email: f.email, password: f.password });
      onCreated && onCreated();
      toast.success("Cont client creat");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  }

  function copyCreds() {
    navigator.clipboard.writeText(`Portal Client Brisk\nEmail: ${created.email}\nParolă temporară: ${created.password}`);
    toast.success("Credențiale copiate");
  }

  const toggleProj = (id) => setProjIds(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  return (
    <div className="fixed inset-0 z-[120] bg-ink/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full p-7 max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="cc-new-client-dialog">
        {!created ? (
          <>
            <div className="text-[20px] font-display font-semibold text-ink mb-1">Utilizator client nou</div>
            <p className="text-[13px] text-ink-muted mb-5">Clientul nu se poate înregistra singur. La prima logare va fi obligat să schimbe parola și să accepte termenii. Va vedea DOAR proiectele bifate.</p>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <L label="Nume persoană"><input value={f.name} onChange={e => setF({ ...f, name: e.target.value })} data-testid="nc-name" className="inp" /></L>
                <L label="Funcție (opțional)"><input value={f.job_title} onChange={e => setF({ ...f, job_title: e.target.value })} className="inp" placeholder="ex: Development Director" /></L>
              </div>
              <L label="Email (user de login)"><input value={f.email} onChange={e => setF({ ...f, email: e.target.value })} data-testid="nc-email" className="inp" /></L>
              <L label="Telefon"><input value={f.phone} onChange={e => setF({ ...f, phone: e.target.value })} className="inp" /></L>
              <L label="Companie (client)">
                {!newCompany ? (
                  <div className="flex gap-2">
                    <select value={f.client_company_id || ""} onChange={e => setF({ ...f, client_company_id: e.target.value })} data-testid="nc-company" className="inp">
                      <option value="">— alege —</option>
                      {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    <button onClick={() => setNewCompany(true)} className="px-3 border border-neutral-200 rounded-lg text-[12px] text-ink-muted hover:border-ink whitespace-nowrap">+ Companie nouă</button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input value={f.company} onChange={e => setF({ ...f, company: e.target.value })} data-testid="nc-company-new" className="inp" placeholder="Numele companiei noi" />
                    <button onClick={() => setNewCompany(false)} className="px-3 border border-neutral-200 rounded-lg text-[12px] text-ink-muted hover:border-ink whitespace-nowrap">Alege existentă</button>
                  </div>
                )}
              </L>
              <L label="Parolă temporară">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <input type={reveal ? "text" : "password"} value={f.password} onChange={e => setF({ ...f, password: e.target.value })} data-testid="nc-password" className="inp pr-9" />
                    <button onClick={() => setReveal(r => !r)} className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-muted">{reveal ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                  </div>
                  <button onClick={() => setF({ ...f, password: genPassword() })} className="px-3 py-1 border border-neutral-200 rounded-lg text-[12px] text-ink-muted hover:border-ink whitespace-nowrap">Regenerează</button>
                </div>
              </L>
              {projects.length > 0 && (
                <L label="Acces la proiecte">
                  <div className="max-h-40 overflow-y-auto border border-neutral-100 rounded-lg divide-y divide-neutral-100">
                    {projects.map(p => (
                      <label key={p.id} className="flex items-center gap-2 px-3 py-2 text-[13px] cursor-pointer hover:bg-neutral-50">
                        <input type="checkbox" checked={projIds.has(p.id)} onChange={() => toggleProj(p.id)} className="h-4 w-4 accent-[#111]" data-testid={`nc-proj-${p.code || p.id}`} />
                        <span className="flex-1 truncate">{p.name}</span>{p.code && <span className="font-mono text-[11px] text-ink-muted">{p.code}</span>}
                      </label>
                    ))}
                  </div>
                </L>
              )}
              <Toggle checked={f.notify} onChange={v => setF({ ...f, notify: v })} label="Trimite credențialele pe email clientului" testid="nc-notify" />
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={onClose} className="px-4 py-2 text-sm text-ink-muted hover:text-ink">Anulează</button>
              <button onClick={submit} disabled={busy} data-testid="nc-submit" className="inline-flex items-center gap-2 px-5 py-2 bg-ink text-white text-sm rounded-lg hover:bg-brand disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} Creează cont</button>
            </div>
          </>
        ) : (
          <div data-testid="nc-created">
            <div className="text-[20px] font-display font-semibold text-ink mb-1">Cont creat ✓</div>
            <p className="text-[13px] text-ink-muted mb-4">Transmite aceste credențiale clientului. Parola nu va mai putea fi afișată din nou (poți însă reseta).</p>
            <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-4 font-mono text-[13px] space-y-1">
              <div><span className="text-ink-muted">Email:</span> {created.email}</div>
              <div><span className="text-ink-muted">Parolă:</span> {created.password}</div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={copyCreds} className="inline-flex items-center gap-2 px-4 py-2 border border-neutral-200 rounded-lg text-sm hover:border-ink"><Copy className="h-4 w-4" /> Copiază</button>
              <button onClick={onClose} data-testid="nc-done" className="px-5 py-2 bg-ink text-white text-sm rounded-lg hover:bg-brand">Gata</button>
            </div>
          </div>
        )}
      </div>
      <style>{INP_STYLE}</style>
    </div>
  );
}
